import { forwardRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { IconCircleTickFilled } from '@xing-com/icons';
import { List } from '@xing-com/list';
import { scale110 } from '@xing-com/tokens';
import { BodyCopy, Headline, Hero } from '@xing-com/typography';

import { NewPerkMarker } from './new-perk-marker';
import { PerkListSkeleton } from './perk-list-skeleton';
import * as Styled from './perk-list.styled';
import type { ProductPerksByProduct } from './perk-list.types';

export type {
  Perk,
  PerkGroup,
  ProductPerks,
  ProductPerksByProduct,
} from './perk-list.types';

export type PerkListProps = {
  activationUrls?: Partial<Record<ProductType, string>>;
  initialProduct: ProductType;
  isLoading?: boolean;
  isProductCardsGroupInView?: boolean;
  onActivate?: (product: ProductType) => void;
  onFeatureTab?: (product: ProductType) => void;
  ownedProducts?: Partial<Record<ProductType, boolean>>;
  perks?: ProductPerksByProduct;
};

const productMessageIds = {
  [ProductType.Premium]: {
    activationCta: 'UPSELL_PURCHASE_PERK_LIST_PREMIUM_CTA',
    title: 'UPSELL_PURCHASE_PERK_LIST_PREMIUM_TITLE',
  },
  [ProductType.Projobs]: {
    activationCta: 'UPSELL_PURCHASE_PERK_LIST_PROJOBS_CTA',
    title: 'UPSELL_PURCHASE_PERK_LIST_PROJOBS_TITLE',
  },
};

export const PerkList = forwardRef<HTMLElement, PerkListProps>(
  function PerkList(
    {
      activationUrls,
      initialProduct,
      isLoading = false,
      isProductCardsGroupInView = false,
      onActivate,
      onFeatureTab,
      ownedProducts,
      perks,
    },
    ref
  ) {
    const [activeProduct, setActiveProduct] = useState(initialProduct);
    const [overViewGroupRef, isOverViewGroupInView] = useInView({
      fallbackInView: true,
      initialInView: true,
    });
    const [activationButtonRef, isActivationButtonInView] = useInView({
      fallbackInView: true,
      initialInView: true,
      // HeaderMinimal height scale210 -- this parameter doesnt accept string interpolation
      rootMargin: '-84px 0px 0px 0px',
    });
    const displayStickyButtonRow =
      !isProductCardsGroupInView &&
      !isOverViewGroupInView &&
      !isActivationButtonInView;

    useEffect(() => {
      setActiveProduct(initialProduct);
    }, [initialProduct]);

    if (isLoading) {
      return <PerkListSkeleton />;
    }

    if (!perks) {
      return null;
    }

    const products = {
      [ProductType.Premium]: {
        messageIds: productMessageIds[ProductType.Premium],
        perks: perks[ProductType.Premium],
      },
      [ProductType.Projobs]: {
        messageIds: productMessageIds[ProductType.Projobs],
        perks: perks[ProductType.Projobs],
      },
    };
    const activeProductDetails = products[activeProduct];
    const isActiveProductOwned = ownedProducts?.[activeProduct] === true;

    return (
      <Styled.Section aria-labelledby="perk-list-heading" ref={ref}>
        {displayStickyButtonRow && !isActiveProductOwned ? (
          <Styled.StickyButtonRow>
            <Styled.StickyCTADesktop
              href={activationUrls?.[activeProduct]}
              onClick={() => onActivate?.(activeProduct)}
              size="large"
              variant="primary"
            >
              <FormattedMessage
                id={activeProductDetails.messageIds.activationCta}
              />
            </Styled.StickyCTADesktop>
            <Styled.StickyCTATablet
              href={activationUrls?.[activeProduct]}
              onClick={() => onActivate?.(activeProduct)}
              size="small"
              variant="primary"
            >
              <FormattedMessage
                id={activeProductDetails.messageIds.activationCta}
              />
            </Styled.StickyCTATablet>
          </Styled.StickyButtonRow>
        ) : null}
        <Styled.Overview ref={overViewGroupRef}>
          <Headline as="h2" id="perk-list-heading" noMargin size="xxlarge">
            <FormattedMessage id="UPSELL_PURCHASE_PERK_LIST_HEADING" />
          </Headline>
          <Styled.ContentSwitcher
            activeItemIndex={activeProduct === ProductType.Premium ? 0 : 1}
            allowDropdown={false}
            data={[
              {
                copy: (
                  <FormattedMessage
                    id={products[ProductType.Premium].messageIds.title}
                  />
                ),
                onClick: () => {
                  onFeatureTab?.(ProductType.Premium);
                  setActiveProduct(ProductType.Premium);
                },
              },
              {
                copy: (
                  <FormattedMessage
                    id={products[ProductType.Projobs].messageIds.title}
                  />
                ),
                onClick: () => {
                  onFeatureTab?.(ProductType.Projobs);
                  setActiveProduct(ProductType.Projobs);
                },
              },
            ]}
            size="medium"
          />
        </Styled.Overview>

        <Styled.ProductHeader>
          <Styled.ProductTitle>
            <Hero as="h3" size="medium" noMargin>
              <FormattedMessage id={activeProductDetails.messageIds.title} />
            </Hero>
          </Styled.ProductTitle>
          <Styled.Intro>
            <BodyCopy noMargin size="large">
              {activeProductDetails.perks.intro}
            </BodyCopy>
          </Styled.Intro>
          {!isActiveProductOwned ? (
            <Styled.ActivationButton
              href={activationUrls?.[activeProduct]}
              innerRef={activationButtonRef}
              onClick={() => onActivate?.(activeProduct)}
              size="large"
              variant="primary"
            >
              <FormattedMessage
                id={activeProductDetails.messageIds.activationCta}
              />
            </Styled.ActivationButton>
          ) : null}
        </Styled.ProductHeader>

        <Styled.Groups data-qa="perk-list">
          {activeProductDetails.perks.groups.map((group) => (
            <Styled.Group key={group.heading}>
              <Headline as="h4" noMargin size="xlarge">
                {group.heading}
              </Headline>
              <List>
                {group.perks.map((perk) => (
                  <Styled.Perk key={perk.text}>
                    <IconCircleTickFilled
                      aria-hidden="true"
                      height={scale110}
                      width={scale110}
                    />
                    <Styled.PerkText>
                      <BodyCopy as="span" noMargin size="medium">
                        {perk.text}
                      </BodyCopy>
                    </Styled.PerkText>
                    {perk.isNew ? <NewPerkMarker /> : null}
                  </Styled.Perk>
                ))}
              </List>
            </Styled.Group>
          ))}
        </Styled.Groups>
      </Styled.Section>
    );
  }
);
